<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account</v-icon>
        User Details
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <!-- User Header -->
        <div class="text-center mb-6">
          <v-avatar size="80" color="#002060" class="mb-3">
            <v-icon size="40" color="white">mdi-account</v-icon>
          </v-avatar>
          <h2 class="user-name mb-2">{{ user.firstName }} {{ user.surname }}</h2>
          <p class="user-email mb-3">{{ user.email }}</p>
        </div>

        <!-- User Details Sections -->
        <v-row>
          <!-- Personal Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Personal Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.firstName || 'N/A'"
              label="First Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.surname || 'N/A'"
              label="Surname"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="formattedIdNumber"
              label="ID Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-card-account-details"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.email || 'N/A'"
              label="Email"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-email"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Address Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Address Information</h3>
          </v-col>
          
          <v-col cols="12">
            <v-text-field
              :model-value="user.address || 'N/A'"
              label="Address"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-home"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.city || 'N/A'"
              label="City"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-city"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.postalCode || 'N/A'"
              label="Postal Code"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-mailbox"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Banking Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Banking Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.bankName || 'N/A'"
              label="Bank Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-bank"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.bankAccountNumber || 'N/A'"
              label="Account Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-credit-card"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- System Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">System Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.dateAdded || 'N/A'"
              label="Date Added"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-calendar"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.status || 'Active'"
              label="Status"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-check-circle"
              readonly
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatIdNumber } from '../utils/idNumberFormatter';

export default {
  name: 'UserDetails',
  components: {
    NotificationDialog
  },
  data() {
    return {
      user: {},
      loading: true,
      error: null,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: ''
    };
  },
  computed: {
    formattedIdNumber() {
      return formatIdNumber(this.user?.idNumber) || 'N/A';
    }
  },
  methods: {
    async loadUser() {
      try {
        this.loading = true;
        this.error = null;
        
        const userId = this.$route.params.id;
        if (!userId) {
          this.error = 'No user ID provided';
          return;
        }

        // Try to load from Firestore first - using 'members' collection
        try {
          const userDoc = await getDoc(doc(db, 'members', userId));
          if (userDoc.exists()) {
            this.user = { id: userId, ...userDoc.data() };
          } else {
            // Fall back to mock data
            this.loadMockUser(userId);
          }
        } catch (dbError) {
          console.warn('Firestore error, using mock data:', dbError);
          this.loadMockUser(userId);
        }
      } catch (err) {
        console.error('Error loading user:', err);
        this.error = 'Failed to load user details';
      } finally {
        this.loading = false;
      }
    },
    
    loadMockUser(userId) {
      // Mock data for demo purposes - matching Members.vue data structure
      const mockUsers = [
        {
          id: 'member1',
          firstName: 'John',
          surname: 'Doe',
          idNumber: '1234567890123',
          email: 'john.doe@example.com',
          address: '123 Main St, Cape Town',
          bankName: 'FNB',
          bankAccountNumber: '1234567890',
          role: 'member',
          status: 'active',
          dateAdded: '06/18/2025'
        },
        {
          id: 'member2',
          firstName: 'Jane',
          surname: 'Smith',
          idNumber: '9876543210987',
          email: 'jane.smith@example.com',
          address: '456 Oak Ave, Johannesburg',
          bankName: 'Standard Bank',
          bankAccountNumber: '0987654321',
          role: 'member',
          status: 'active',
          dateAdded: '06/17/2025'
        }
      ];
      
      this.user = mockUsers.find(u => u.id === userId) || mockUsers[0];
    },
    
    editUser() {
      // TODO: Implement edit functionality
      console.log('Edit user:', this.user.id);
      // this.$router.push({ name: 'EditUser', params: { id: this.user.id } });
    }
  },
  async created() {
    await this.loadUser();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.user-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.user-email {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 